"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Marke } from "./marke";

export interface SidebarEintrag {
  id: string;
  titel: string;
  href: string;
  /** Pfad-Präfixe, unter denen der Eintrag als aktiv gilt (wie bisher in der Tab-Zeile). */
  aktivPraefixe: string[];
  /** Symbol, serverseitig gerendert (kommt aus der Navigationsquelle). */
  icon: React.ReactNode;
}

/**
 * Seitenleiste der neuen Shell (Desktop fest, Mobile als Offcanvas über
 * Preline-Overlay `#hs-pro-sidebar`).
 *
 * Einträge und Sichtbarkeit kommen 1:1 aus derselben Quelle wie die bisherige
 * Tab-Zeile (buildMainNav → applyRailPrefs im Layout): „Heute" zuerst, dann die
 * Bereiche in persönlicher Reihenfolge; persönlich ausgeblendete Bereiche
 * fehlen, außer dem gerade aktiven (gleiche Regel wie bisher).
 */
export function ShellSidebar({
  eintraege,
  versteckteIds,
  hornaut,
}: {
  eintraege: SidebarEintrag[];
  versteckteIds: string[];
  /** Hornaut-Einstieg (oben in der Seitenleiste), bereits KI-Gate-entschieden. */
  hornaut: React.ReactNode;
}) {
  const pathname = usePathname() ?? "";
  const aktiv = eintraege.find((e) => e.aktivPraefixe.some((p) => pathname.startsWith(p)));
  const versteckt = new Set(versteckteIds);
  const sichtbar = eintraege.filter((e) => e.id === "heute" || !versteckt.has(e.id) || e.id === aktiv?.id);

  return (
    <>
      {/* Preline: dashboards/admin-dashboard/index.html#main-sidebar */}
      <aside
        id="hs-pro-sidebar"
        className="hs-overlay [--auto-close:lg] hs-overlay-open:translate-x-0 -translate-x-full transition-all duration-300 transform w-65 h-full hidden fixed inset-y-0 inset-s-0 z-60 bg-sidebar border-e border-sidebar-line lg:block lg:translate-x-0 lg:inset-e-auto lg:bottom-0"
        tabIndex={-1}
        aria-label="Hauptnavigation"
      >
        <div className="relative flex flex-col h-full max-h-full pt-3">
          <header className="h-11.5 ps-5 pe-2 lg:ps-8 flex items-center gap-x-1">
            {/* Preline: dashboards/admin-dashboard/index.html#main-sidebar (Logo-Slot) */}
            <Marke groesse="klein" href="/heute" />
            {/* End Logo */}

            <div className="lg:hidden ms-auto">
              {/* Sidebar Close */}
              <button
                type="button"
                className="w-6 h-7 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md bg-layer border border-layer-line text-layer-foreground shadow-2xs hover:bg-layer-hover disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-layer-focus"
                data-hs-overlay="#hs-pro-sidebar"
                aria-label="Navigation schließen"
              >
                <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="7 8 3 12 7 16" />
                  <line x1="21" x2="11" y1="12" y2="12" />
                  <line x1="21" x2="11" y1="6" y2="6" />
                  <line x1="21" x2="11" y1="18" y2="18" />
                </svg>
              </button>
              {/* End Sidebar Close */}
            </div>
          </header>

          {/* Content */}
          <div className="mt-1.5 h-full overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb">
            {hornaut}

            {/* Nav */}
            <nav className="pb-3 w-full flex flex-col flex-wrap" aria-label="Module">
              <ul className="flex flex-col gap-y-1">
                {sichtbar.map((e) => {
                  const istAktiv = e.id === aktiv?.id;
                  return (
                    // Link
                    <li key={e.id} className="px-2 lg:px-5">
                      <Link
                        className={
                          istAktiv
                            ? "flex gap-x-3 py-2 px-3 text-sm text-sidebar-nav-foreground rounded-lg hover:bg-sidebar-nav-hover focus:outline-hidden focus:bg-sidebar-nav-focus bg-sidebar-nav-active"
                            : "flex gap-x-3 py-2 px-3 text-sm text-sidebar-nav-foreground rounded-lg hover:bg-sidebar-nav-hover focus:outline-hidden focus:bg-sidebar-nav-focus"
                        }
                        href={e.href}
                        aria-current={istAktiv ? "page" : undefined}
                      >
                        {e.icon}
                        {e.titel}
                      </Link>
                    </li>
                    // End Link
                  );
                })}
              </ul>
            </nav>
            {/* End Nav */}
          </div>
          {/* End Content */}
        </div>
      </aside>
      {/* End Sidebar */}
    </>
  );
}
